import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  RefreshControl,
  Animated,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatCurrency } from '../services/paymentGateway';
import {
  getTournamentById,
  updateTournamentPairing as updatePairingSupabase,
  updateTournamentParticipant as updateParticipantSupabase,
} from '../services/database';

const TournamentPlayerScreen = ({ route, navigation }) => {
  const { tournamentId } = route.params || {};
  const { user } = useAuth();
  const { showToast } = useToast();

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submittingResult, setSubmittingResult] = useState(false);
  
  // Animation for waiting state
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const autoRefreshInterval = useRef(null);

  useEffect(() => {
    if (tournamentId) {
      fetchTournament(tournamentId);
      
      // Auto-refresh every 10 seconds for live updates
      autoRefreshInterval.current = setInterval(() => {
        fetchTournament(tournamentId, true);
      }, 10000);
      
      return () => {
        if (autoRefreshInterval.current) {
          clearInterval(autoRefreshInterval.current);
        }
      };
    } else {
      showToast('Open this view from a tournament link', 'info');
      setLoading(false);
    }
  }, [tournamentId]);

  // Pulse animation for waiting state
  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    );
    pulse.start();
    return () => pulse.stop();
  }, []);

  const fetchTournament = async (id, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const raw = await getTournamentById(id);
      // Map to camelCase for UI
      const mapped = {
        ...raw,
        currentRound: raw.current_round,
        totalRounds: raw.total_rounds,
        allowPhones: raw.allow_phones,
        isPaid: raw.is_paid,
        entryFee: raw.entry_fee,
        formatConfig: raw.format_config,
        club: raw.club ? { ...raw.club, clubName: raw.club.club_name } : null,
        participants: {
          items: (raw.participants || []).map(p => ({
            ...p,
            userId: p.user_id,
            guestName: p.guest_name,
            currentBoard: p.current_board,
            seedNumber: p.seed_number,
            hasPaid: p.has_paid,
            paymentStatus: p.payment_status,
            amountPaid: p.amount_paid,
          })),
        },
        pairings: {
          items: (raw.pairings || []).map(p => ({
            ...p,
            roundNumber: p.round_number,
            boardNumber: p.board_number,
            tableLocation: p.table_location,
            whiteParticipantId: p.white_participant_id,
            blackParticipantId: p.black_participant_id,
            whiteParticipant: p.white_participant ? { id: p.white_participant.id, guestName: p.white_participant.guest_name } : null,
            blackParticipant: p.black_participant ? { id: p.black_participant.id, guestName: p.black_participant.guest_name } : null,
          })),
        },
        standings: { items: [] }, // standings computed from participants
      };
      setTournament(mapped);
    } catch (error) {
      console.error('Error loading player view:', error);
      if (!silent) showToast('Unable to load tournament', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchTournament(tournamentId);
  };

  const handleReportResult = async (outcome) => {
    if (!myParticipant || !myPairing) return;
    const isWhite = myPairing.whiteParticipantId === myParticipant.id;
    let pairingResult = outcome;
    if (outcome === 'PLAYER_WIN') {
      pairingResult = isWhite ? 'WHITE_WIN' : 'BLACK_WIN';
    } else if (outcome === 'PLAYER_LOSS') {
      pairingResult = isWhite ? 'BLACK_WIN' : 'WHITE_WIN';
    } else if (outcome === 'DRAW') {
      pairingResult = 'DRAW';
    }

    const opponentId =
      myPairing.whiteParticipantId === myParticipant.id
        ? myPairing.blackParticipantId
        : myPairing.whiteParticipantId;
    const opponent = opponentId ? participantById[opponentId] : null;
    const resultScoreMap = {
      WHITE_WIN: [1, 0],
      BLACK_WIN: [0, 1],
      DRAW: [0.5, 0.5],
      DOUBLE_FORFEIT: [0, 0],
    };
    const [whiteDelta, blackDelta] = resultScoreMap[pairingResult] || [0, 0];
    const [prevWhiteDelta, prevBlackDelta] = resultScoreMap[myPairing.result] || [0, 0];
    const whiteDiff = whiteDelta - prevWhiteDelta;
    const blackDiff = blackDelta - prevBlackDelta;

    try {
      setSubmittingResult(true);
      await updatePairingSupabase(myPairing.id, { result: pairingResult });

      const updates = [];
      if (whiteDiff !== 0 && myPairing.whiteParticipantId) {
        const white = participantById[myPairing.whiteParticipantId];
        updates.push(updateParticipantSupabase(white.id, { score: (white.score || 0) + whiteDiff }));
      }
      if (blackDiff !== 0 && myPairing.blackParticipantId) {
        const black = participantById[myPairing.blackParticipantId];
        updates.push(updateParticipantSupabase(black.id, { score: (black.score || 0) + blackDiff }));
      }
      await Promise.all(updates);
      showToast('Result submitted', 'success');
      fetchTournament(tournament.id);
    } catch (error) {
      console.error('Error reporting result:', error);
      showToast('Failed to submit result', 'error');
    } finally {
      setSubmittingResult(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!tournament) {
    return (
      <View style={styles.centered}>
        <Icon name="error-outline" size={48} color="#ccc" />
        <Text style={styles.loadingText}>Tournament not found</Text>
      </View>
    );
  }

  const participants = tournament.participants?.items || [];
  const standings =
    tournament.standings?.items
      ?.filter(Boolean)
      ?.sort((a, b) => (a.rank || 999) - (b.rank || 999)) || [];

  const derivedStandings = standings.length
    ? standings
    : participants
        .map((participant) => ({
          id: participant.id,
          rank: 0,
          score: participant.score || 0,
          tieBreak1: participant.seedNumber ? 1000 - participant.seedNumber : 0,
          participant,
        }))
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return (a.tieBreak1 || 0) - (b.tieBreak1 || 0);
        })
        .map((item, index) => ({ ...item, rank: index + 1 }));

  const latestRound = tournament.currentRound || 1;
  const pairings =
    tournament.pairings?.items?.filter((p) => p.roundNumber === latestRound) || [];

  const formatSettings = useMemo(() => {
    if (!tournament?.formatConfig) return {};
    try {
      return JSON.parse(tournament.formatConfig) || {};
    } catch (error) {
      console.warn('Invalid tournament formatConfig', error);
      return {};
    }
  }, [tournament]);
  const announcements = formatSettings.announcements || [];

  const participantById = useMemo(() => {
    const map = {};
    participants.forEach((p) => {
      if (p?.id) {
        map[p.id] = p;
      }
    });
    return map;
  }, [participants]);

  const myParticipant =
    participants.find((p) => p.userId === user?.attributes?.sub) || participants[0];

  const myPairing =
    myParticipant &&
    tournament.pairings?.items?.find(
      (pair) =>
        pair.roundNumber === latestRound &&
        (pair.whiteParticipantId === myParticipant.id ||
          pair.blackParticipantId === myParticipant.id)
    );
  const formatAnnouncementTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Determine tournament state for UI
  const isWaitingToStart = tournament.status === 'REGISTRATION' || tournament.status === 'DRAFT';
  const isInProgress = tournament.status === 'IN_PROGRESS';
  const isCompleted = tournament.status === 'COMPLETED';
  
  // Find my standing
  const myStanding = derivedStandings.find(s => s.participant?.id === myParticipant?.id);
  const myRank = myStanding?.rank || '-';

  // Find my opponent for current round
  const myOpponent = myPairing
    ? myPairing.whiteParticipantId === myParticipant?.id
      ? myPairing.blackParticipant
      : myPairing.whiteParticipant
    : null;
  const amPlayingWhite = myPairing?.whiteParticipantId === myParticipant?.id;

  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Header with back button */}
      <View style={styles.hero}>
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => navigation?.goBack()}
        >
          <Icon name="arrow-back" size={24} color="white" />
        </TouchableOpacity>
        <Text style={styles.heroTitle}>{tournament.name}</Text>
        <Text style={styles.heroSubtitle}>
          {isWaitingToStart ? 'Registration Open' : `Round ${latestRound}/${tournament.totalRounds || '?'}`} • {tournament.type.replace('_', ' ')} •{' '}
          {tournament.isPaid ? formatCurrency(tournament.entryFee || 0) : 'Free'}
        </Text>
        <View style={[styles.statusBadge, styles[`status${tournament.status}`]]}>
          <Text style={styles.statusText}>{tournament.status?.replace('_', ' ')}</Text>
        </View>
      </View>

      {/* Waiting for Tournament to Start State */}
      {isWaitingToStart && (
        <Animated.View style={[styles.waitingCard, { transform: [{ scale: pulseAnim }] }]}>
          <View style={styles.waitingIconContainer}>
            <Icon name="hourglass-empty" size={48} color="#FF9800" />
          </View>
          <Text style={styles.waitingTitle}>Waiting for Tournament to Start</Text>
          <Text style={styles.waitingSubtitle}>
            The tournament director will start the event soon. You'll see your pairing here when Round 1 begins.
          </Text>
          <View style={styles.waitingStats}>
            <View style={styles.waitingStat}>
              <Text style={styles.waitingStatValue}>{participants.length}</Text>
              <Text style={styles.waitingStatLabel}>Registered</Text>
            </View>
            <View style={styles.waitingStatDivider} />
            <View style={styles.waitingStat}>
              <Text style={styles.waitingStatValue}>{tournament.totalRounds || '?'}</Text>
              <Text style={styles.waitingStatLabel}>Rounds</Text>
            </View>
            <View style={styles.waitingStatDivider} />
            <View style={styles.waitingStat}>
              <Text style={styles.waitingStatValue}>
                {(() => {
                  try {
                    const config = JSON.parse(tournament.formatConfig || '{}');
                    return config.timeControl || 'TBD';
                  } catch {
                    return 'TBD';
                  }
                })()}
              </Text>
              <Text style={styles.waitingStatLabel}>Time Control</Text>
            </View>
          </View>
          <Text style={styles.autoRefreshNote}>
            ↻ Auto-refreshing every 10 seconds
          </Text>
        </Animated.View>
      )}

      {/* Your Current Standing - Always visible when in progress */}
      {isInProgress && myParticipant && (
        <View style={styles.myStandingCard}>
          <View style={styles.myStandingHeader}>
            <View style={styles.myRankBadge}>
              <Text style={styles.myRankText}>#{myRank}</Text>
            </View>
            <View style={styles.myStandingInfo}>
              <Text style={styles.myStandingName}>Your Standing</Text>
              <Text style={styles.myStandingScore}>
                {myParticipant.score?.toFixed(1) || '0.0'} points
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Your Next Pairing - Prominent when in progress */}
      {isInProgress && myPairing && myParticipant && (
        <View style={styles.nextPairingCard}>
          <View style={styles.nextPairingHeader}>
            <Icon name="sports-esports" size={24} color="#2E7D32" />
            <Text style={styles.nextPairingTitle}>Your Round {latestRound} Pairing</Text>
          </View>
          
          <View style={styles.pairingMatchup}>
            <View style={[styles.playerSide, amPlayingWhite && styles.playerSideYou]}>
              <View style={[styles.pieceIndicator, { backgroundColor: '#fff', borderWidth: 2, borderColor: '#333' }]}>
                <Text style={{ fontSize: 20 }}>♔</Text>
              </View>
              <Text style={styles.playerSideName}>
                {myPairing.whiteParticipant?.guestName || 'White'}
              </Text>
              {amPlayingWhite && <Text style={styles.youLabel}>YOU</Text>}
            </View>
            
            <View style={styles.vsContainer}>
              <Text style={styles.vsText}>VS</Text>
              <Text style={styles.boardText}>Board {myPairing.boardNumber}</Text>
            </View>
            
            <View style={[styles.playerSide, !amPlayingWhite && styles.playerSideYou]}>
              <View style={[styles.pieceIndicator, { backgroundColor: '#333' }]}>
                <Text style={{ fontSize: 20, color: '#fff' }}>♚</Text>
              </View>
              <Text style={styles.playerSideName}>
                {myPairing.blackParticipant?.guestName || 'Black'}
              </Text>
              {!amPlayingWhite && <Text style={styles.youLabel}>YOU</Text>}
            </View>
          </View>

          {myPairing.tableLocation && (
            <View style={styles.tableLocation}>
              <Icon name="place" size={16} color="#666" />
              <Text style={styles.tableLocationText}>{myPairing.tableLocation}</Text>
            </View>
          )}

          {/* Result already recorded */}
          {myPairing.result && (
            <View style={styles.resultRecorded}>
              <Icon name="check-circle" size={20} color="#2E7D32" />
              <Text style={styles.resultRecordedText}>
                Result: {myPairing.result.replace('_', ' ')}
              </Text>
            </View>
          )}
        </View>
      )}

      {tournament.allowPhones ? (
        <>
          {/* Report Result Section - Only if no result yet */}
          {isInProgress && myPairing && !myPairing.result && myParticipant && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Report Result</Text>
              {(!tournament.isPaid || myParticipant?.hasPaid) ? (
                <View style={styles.reportResultCard}>
                  <Text style={styles.helperText}>
                    Select the outcome of your game. The tournament director can override if needed.
                  </Text>
                  <View style={styles.reportButtons}>
                    <TouchableOpacity
                      style={[styles.reportButton, styles.reportButtonWin]}
                      onPress={() => handleReportResult('PLAYER_WIN')}
                      disabled={submittingResult}
                    >
                      <Icon name="emoji-events" size={22} color="#2E7D32" />
                      <Text style={[styles.reportButtonText, { color: '#2E7D32' }]}>I Won</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.reportButton, styles.reportButtonDraw]}
                      onPress={() => handleReportResult('DRAW')}
                      disabled={submittingResult}
                    >
                      <Icon name="handshake" size={22} color="#FF9800" />
                      <Text style={[styles.reportButtonText, { color: '#FF9800' }]}>Draw</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.reportButton, styles.reportButtonLoss]}
                      onPress={() => handleReportResult('PLAYER_LOSS')}
                      disabled={submittingResult}
                    >
                      <Icon name="thumb-down" size={22} color="#C62828" />
                      <Text style={[styles.reportButtonText, { color: '#C62828' }]}>I Lost</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <Text style={styles.helperText}>
                  Payment pending. Please settle entry fee with the director.
                </Text>
              )}
            </View>
          )}

          <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>All Round {latestRound} Pairings</Text>
            <Text style={styles.helperText}>{pairings.length} games</Text>
          </View>
            {pairings.length === 0 ? (
              <View style={styles.emptyState}>
                <Icon name="repeat" size={40} color="#ccc" />
                <Text style={styles.emptyTitle}>Pairings not posted yet</Text>
              </View>
            ) : (
              pairings.map((pairing) => (
                <TouchableOpacity
                  key={pairing.id}
                  style={styles.pairingRow}
                  onPress={() =>
                    showToast(`Board ${pairing.boardNumber} • ${pairing.tableLocation || 'TBD'}`, 'info')
                  }
                >
                  <View style={styles.boardBadge}>
                    <Text style={styles.boardBadgeText}>{pairing.boardNumber}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.playerName}>
                      {pairing.whiteParticipant?.guestName || 'White TBD'}
                    </Text>
                    <Text style={styles.playerName}>
                      {pairing.blackParticipant?.guestName || 'Black TBD'}
                    </Text>
                  </View>
                  <Icon name="chevron-right" size={24} color="#999" />
                </TouchableOpacity>
              ))
            )}
          </View>
        </>
      ) : (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Phones Disabled</Text>
          <Text style={styles.detailText}>
            The tournament director has disabled player devices. Please check the projector or
            pairing board for updates and report results in person.
          </Text>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Standings</Text>
        {derivedStandings.length === 0 ? (
          <View style={styles.emptyState}>
            <Icon name="leaderboard" size={40} color="#ccc" />
            <Text style={styles.emptyTitle}>Standings coming soon</Text>
          </View>
        ) : (
          derivedStandings.map((row) => (
            <View key={row.id} style={styles.standingRow}>
              <Text style={styles.rankBadge}>{row.rank || '-'}</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.playerName}>
                  {row.participant?.guestName || 'Player'}
                </Text>
                <Text style={styles.playerSubtext}>
                  {row.score?.toFixed(1) || 0} pts • TB1 {row.tieBreak1 || 0}
                </Text>
              </View>
            </View>
          ))
        )}
      </View>

      {announcements.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Announcements</Text>
          {announcements.slice(0, 5).map((announcement) => (
            <View key={announcement.id} style={styles.announcementRow}>
              <Icon name="campaign" size={18} color="#1976D2" />
              <View style={{ marginLeft: 12 }}>
                <Text style={styles.playerName}>{announcement.message}</Text>
                <Text style={styles.playerSubtext}>
                  {formatAnnouncementTime(announcement.createdAt)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}

      <View style={{ height: 40 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  hero: {
    backgroundColor: '#1F2937',
    paddingTop: Platform.OS === 'ios' ? 60 : 30,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  backButton: {
    marginBottom: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: 'white',
  },
  heroSubtitle: {
    fontSize: 15,
    color: '#9CA3AF',
    marginTop: 4,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 12,
    textTransform: 'capitalize',
  },
  statusREGISTRATION: {
    backgroundColor: '#2196F3',
  },
  statusDRAFT: {
    backgroundColor: '#9E9E9E',
  },
  statusIN_PROGRESS: {
    backgroundColor: '#4CAF50',
  },
  statusCOMPLETED: {
    backgroundColor: '#795548',
  },
  // Waiting state styles
  waitingCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  waitingIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#FFF3E0',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  waitingTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 8,
  },
  waitingSubtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  waitingStats: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    backgroundColor: '#F5F5F5',
    borderRadius: 12,
    width: '100%',
  },
  waitingStat: {
    flex: 1,
    alignItems: 'center',
  },
  waitingStatValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  waitingStatLabel: {
    fontSize: 11,
    color: '#666',
    marginTop: 4,
    textTransform: 'uppercase',
  },
  waitingStatDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#DDD',
  },
  autoRefreshNote: {
    marginTop: 16,
    fontSize: 12,
    color: '#999',
  },
  // My standing card
  myStandingCard: {
    backgroundColor: '#E8F5E9',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#4CAF50',
  },
  myStandingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  myRankBadge: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#4CAF50',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  myRankText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  myStandingInfo: {
    flex: 1,
  },
  myStandingName: {
    fontSize: 14,
    color: '#666',
  },
  myStandingScore: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  // Next pairing card
  nextPairingCard: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 20,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 2,
    borderColor: '#4CAF50',
  },
  nextPairingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  nextPairingTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  pairingMatchup: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
  },
  playerSide: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#F5F5F5',
  },
  playerSideYou: {
    backgroundColor: '#E8F5E9',
    borderWidth: 2,
    borderColor: '#4CAF50',
  },
  pieceIndicator: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  playerSideName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  youLabel: {
    marginTop: 4,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#4CAF50',
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  vsContainer: {
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  vsText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#999',
  },
  boardText: {
    fontSize: 11,
    color: '#999',
    marginTop: 4,
  },
  tableLocation: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    gap: 4,
  },
  tableLocationText: {
    fontSize: 13,
    color: '#666',
  },
  resultRecorded: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E0E0E0',
    gap: 8,
  },
  resultRecordedText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E7D32',
  },
  // Report result buttons
  reportButtonWin: {
    backgroundColor: '#E8F5E9',
    borderColor: '#4CAF50',
  },
  reportButtonDraw: {
    backgroundColor: '#FFF3E0',
    borderColor: '#FF9800',
  },
  reportButtonLoss: {
    backgroundColor: '#FFEBEE',
    borderColor: '#F44336',
  },
  section: {
    backgroundColor: 'white',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: Platform.OS === 'ios' ? 0.08 : 0.2,
    shadowRadius: 10,
    elevation: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#333',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  helperText: {
    fontSize: 12,
    color: '#666',
  },
  detailText: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
    backgroundColor: '#2E7D32',
    padding: 12,
    borderRadius: 10,
  },
  primaryButtonText: {
    color: 'white',
    fontWeight: '600',
  },
  reportResultCard: {
    marginTop: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 12,
    backgroundColor: '#F9FAFB',
  },
  reportTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 8,
    color: '#333',
  },
  reportButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  reportButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 10,
    marginHorizontal: 4,
    backgroundColor: '#fff',
  },
  reportButtonText: {
    marginTop: 4,
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  pairingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  boardBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#E0F2F1',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  boardBadgeText: {
    fontWeight: '700',
    color: '#00796B',
  },
  standingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f3f3',
  },
  rankBadge: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#E8F5E9',
    textAlign: 'center',
    textAlignVertical: 'center',
    color: '#2E7D32',
    fontWeight: '700',
    marginRight: 12,
  },
  playerName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
  },
  playerSubtext: {
    fontSize: 13,
    color: '#777',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  announcementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f5f5f5',
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#777',
    marginTop: 8,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 10,
    color: '#666',
  },
});

export default TournamentPlayerScreen;

